import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, Building2, TrendingUp, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { StateChoroplethMap } from './StateChoroplethMap';
import { getGrade, getHospitalGrade, gradeColor } from '@/lib/grades';
import type { StateChargemasterStats, BaselineType } from '@/hooks/useStateChargemasterStats';

interface StateChargemasterViewProps {
  stats: StateChargemasterStats[];
  nationalStats: {
    medianMarkup: number;
    medianMarkupMB: number;
    totalHospitals: number;
    mostExpensive: StateChargemasterStats;
    cheapest: StateChargemasterStats;
  } | null;
  selectedState: string | null;
  onStateSelect: (state: string | null) => void;
}

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

function formatPct(v: number | null): string {
  if (v == null) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}

/** Get the display value for a row based on baseline type */
function rowMedianMarkup(row: StateChargemasterStats, baseline: BaselineType): number {
  return baseline === 'median' ? row.medianMarkupMB : row.medianMarkup;
}
function rowAvgMarkup(row: StateChargemasterStats, baseline: BaselineType): number {
  return baseline === 'median' ? row.avgMarkupMB : row.avgMarkup;
}
function rowCodeMedian(row: StateChargemasterStats, code: 'cpt' | 'drg' | 'rev' | 'drug', baseline: BaselineType): number | null {
  if (baseline === 'median') {
    switch (code) {
      case 'cpt': return row.cptMedianMB;
      case 'drg': return row.drgMedianMB;
      case 'rev': return row.revMedianMB;
      case 'drug': return row.drugMedianMB;
    }
  }
  switch (code) {
    case 'cpt': return row.cptMedian;
    case 'drg': return row.drgMedian;
    case 'rev': return row.revMedian;
    case 'drug': return row.drugMedian;
  }
}
function rowGrade(row: StateChargemasterStats, baseline: BaselineType) {
  // Each baseline family has its own calibrated scale — median-baseline
  // values run higher, so they grade on the hospital-level thresholds.
  return baseline === 'median'
    ? getHospitalGrade(rowMedianMarkup(row, baseline))
    : getGrade(rowMedianMarkup(row, baseline));
}
function rowGradeDist(row: StateChargemasterStats, baseline: BaselineType) {
  if (baseline === 'median') {
    return { A: row.gradeAMB, B: row.gradeBMB, C: row.gradeCMB, D: row.gradeDMB, F: row.gradeFMB };
  }
  return { A: row.gradeA, B: row.gradeB, C: row.gradeC, D: row.gradeD, F: row.gradeF };
}
function rowBestHospital(row: StateChargemasterStats, baseline: BaselineType) {
  return baseline === 'median' ? row.bestHospitalMB : row.bestHospital;
}
function rowWorstHospital(row: StateChargemasterStats, baseline: BaselineType) {
  return baseline === 'median' ? row.worstHospitalMB : row.worstHospital;
}

type SortKey = 'state' | 'hospitalCount' | 'medianMarkup' | 'avgMarkup' | 'grade' |
  'cptMedian' | 'drgMedian' | 'revMedian' | 'drugMedian';

export function StateChargemasterView({
  stats,
  nationalStats,
  selectedState,
  onStateSelect,
}: StateChargemasterViewProps) {
  const { t } = useTranslation(['rankings', 'common']);
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';
  const navigate = useNavigate();

  const [sortKey, setSortKey] = useState<SortKey>('medianMarkup');
  const [sortAsc, setSortAsc] = useState(false);
  const [showCodeCols, setShowCodeCols] = useState(true);
  // Default to the MEDIAN baseline: comparing each hospital to the national
  // *mean* price makes almost every hospital look "below average" because the
  // mean is inflated by a few extreme-outlier hospitals (right-skewed prices).
  // The median baseline is robust to those outliers and reflects how the typical
  // hospital compares to the typical price — the honest headline. The "vs Mean"
  // view stays available via the toggle for anyone who wants it.
  const [baseline, setBaseline] = useState<BaselineType>('median');

  // Map data for choropleth
  const mapData = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of stats) m[s.state] = rowMedianMarkup(s, baseline);
    return m;
  }, [stats, baseline]);

  // Re-sort stats for median baseline (most expensive first)
  const sortedByBaseline = useMemo(() => {
    return [...stats].sort((a, b) => rowMedianMarkup(b, baseline) - rowMedianMarkup(a, baseline));
  }, [stats, baseline]);

  // Sorted data
  const sorted = useMemo(() => {
    const copy = [...sortedByBaseline];
    copy.sort((a, b) => {
      let va: number | string;
      let vb: number | string;
      switch (sortKey) {
        case 'state': va = a.state; vb = b.state; break;
        case 'hospitalCount': va = a.hospitalCount; vb = b.hospitalCount; break;
        case 'medianMarkup': va = rowMedianMarkup(a, baseline); vb = rowMedianMarkup(b, baseline); break;
        case 'avgMarkup': va = rowAvgMarkup(a, baseline); vb = rowAvgMarkup(b, baseline); break;
        case 'grade': va = rowMedianMarkup(a, baseline); vb = rowMedianMarkup(b, baseline); break;
        case 'cptMedian': va = rowCodeMedian(a, 'cpt', baseline) ?? -999; vb = rowCodeMedian(b, 'cpt', baseline) ?? -999; break;
        case 'drgMedian': va = rowCodeMedian(a, 'drg', baseline) ?? -999; vb = rowCodeMedian(b, 'drg', baseline) ?? -999; break;
        case 'revMedian': va = rowCodeMedian(a, 'rev', baseline) ?? -999; vb = rowCodeMedian(b, 'rev', baseline) ?? -999; break;
        case 'drugMedian': va = rowCodeMedian(a, 'drug', baseline) ?? -999; vb = rowCodeMedian(b, 'drug', baseline) ?? -999; break;
        default: va = rowMedianMarkup(a, baseline); vb = rowMedianMarkup(b, baseline);
      }
      if (typeof va === 'string') return sortAsc ? va.localeCompare(vb as string) : (vb as string).localeCompare(va);
      return sortAsc ? (va as number) - (vb as number) : (vb as number) - (va as number);
    });
    return copy;
  }, [sortedByBaseline, sortKey, sortAsc, baseline]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(key === 'state'); }
  };

  const SortIcon = ({ k }: { k: SortKey }) => (
    <span className="inline-flex ml-1 opacity-50">
      {sortKey === k ? (sortAsc ? <ChevronUp size={14} /> : <ChevronDown size={14} />) : <ArrowUpDown size={12} />}
    </span>
  );

  // National stats adjusted for baseline
  const natMedMarkup = nationalStats
    ? (baseline === 'median' ? nationalStats.medianMarkupMB : nationalStats.medianMarkup)
    : 0;
  const natMostExpensive = sortedByBaseline[0];
  const natCheapest = sortedByBaseline[sortedByBaseline.length - 1];

  // Colors
  const cardBg = isAhp
    ? (isDark ? 'rgba(10,22,40,0.85)' : 'rgba(255,255,255,0.95)')
    : 'rgba(0,0,0,0.85)';
  const borderColor = isAhp
    ? (isDark ? 'rgba(197,165,90,0.2)' : 'rgba(0,0,0,0.1)')
    : 'rgba(255,255,255,0.1)';
  const textColor = isAhp ? (isDark ? '#FAF9F6' : '#1B1B1B') : '#fff';
  const mutedText = isAhp ? (isDark ? 'rgba(250,249,246,0.6)' : 'rgba(0,0,0,0.5)') : 'rgba(255,255,255,0.5)';

  const getMarkupBg = (markup: number) => {
    if (markup <= -10) return 'rgba(34,197,94,0.25)';
    if (markup < 10) return 'rgba(64,64,64,0.15)';
    if (markup < 50) return 'rgba(239,68,68,0.2)';
    return 'rgba(239,68,68,0.45)';
  };

  const baselineLabel = baseline === 'median' ? t('stateComparison.chargemaster.vsMedian') : t('stateComparison.chargemaster.vsMean');

  return (
    <div className="space-y-6">
      {/* Choropleth */}
      <div className="rounded-xl p-4" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
        <StateChoroplethMap
          data={mapData}
          selectedState={selectedState}
          onStateClick={(s) => onStateSelect(s === selectedState ? null : s)}
          valueLabel={t('stateComparison.chargemaster.medianMarkupLabel', { baseline: baselineLabel })}
        />
      </div>

      {/* Summary cards */}
      {nationalStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: t('stateComparison.chargemaster.nationalMedianLabel', { baseline: baselineLabel }), value: formatPct(natMedMarkup), icon: TrendingUp },
            { label: t('stateComparison.chargemaster.totalHospitals'), value: nationalStats.totalHospitals.toLocaleString(), icon: Building2 },
            { label: t('stateComparison.chargemaster.mostExpensive'), value: natMostExpensive ? `${natMostExpensive.state} (${formatPct(rowMedianMarkup(natMostExpensive, baseline))})` : '—', icon: TrendingUp },
            { label: t('stateComparison.chargemaster.leastExpensive'), value: natCheapest ? `${natCheapest.state} (${formatPct(rowMedianMarkup(natCheapest, baseline))})` : '—', icon: TrendingDown },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-lg p-3"
              style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}
            >
              <div className="text-xs" style={{ color: mutedText }}>{card.label}</div>
              <div className="text-lg font-bold mt-1" style={{ color: textColor }}>{card.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Controls: baseline toggle + code columns toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Baseline toggle */}
        <div
          className="inline-flex rounded-full p-0.5"
          style={{ backgroundColor: 'rgba(128,128,128,0.15)' }}
        >
          {(['mean', 'median'] as BaselineType[]).map((b) => {
            const active = baseline === b;
            return (
              <button
                key={b}
                onClick={() => setBaseline(b)}
                className="text-xs px-3 py-1.5 rounded-full transition-colors"
                style={{
                  backgroundColor: active ? (isAhp ? 'rgba(197,165,90,0.2)' : 'rgba(0,255,65,0.15)') : 'transparent',
                  color: active ? (isAhp ? '#C5A55A' : '#00ff41') : mutedText,
                  border: active ? `1px solid ${isAhp ? 'rgba(197,165,90,0.3)' : 'rgba(0,255,65,0.2)'}` : '1px solid transparent',
                }}
              >
                {b === 'mean' ? t('stateComparison.chargemaster.vsMean') : t('stateComparison.chargemaster.vsMedian')}
              </button>
            );
          })}
        </div>
        <span className="text-xs" style={{ color: mutedText }}>
          {baseline === 'median'
            ? t('stateComparison.chargemaster.baselineMedianDescription')
            : t('stateComparison.chargemaster.baselineMeanDescription')}
        </span>
        <div className="flex-1" />
        <button
          onClick={() => setShowCodeCols(!showCodeCols)}
          className="text-xs px-3 py-1.5 rounded-full transition-colors"
          style={{
            backgroundColor: showCodeCols ? (isAhp ? 'rgba(197,165,90,0.2)' : 'rgba(0,255,65,0.15)') : 'rgba(128,128,128,0.15)',
            color: showCodeCols ? (isAhp ? '#C5A55A' : '#00ff41') : mutedText,
            border: `1px solid ${showCodeCols ? (isAhp ? 'rgba(197,165,90,0.3)' : 'rgba(0,255,65,0.2)') : 'transparent'}`,
          }}
        >
          {showCodeCols ? t('stateComparison.chargemaster.hideCodeBreakdown') : t('stateComparison.chargemaster.showCodeBreakdown')}
        </button>
      </div>

      {/* Sortable table */}
      <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${borderColor}` }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ color: textColor }}>
            <thead>
              <tr style={{ backgroundColor: isAhp ? (isDark ? 'rgba(0,20,60,0.8)' : 'rgba(0,40,104,0.08)') : 'rgba(0,0,0,0.6)' }}>
                <th className="text-left px-3 py-2.5 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('state')}>
                  {t('stateComparison.chargemaster.state')}<SortIcon k="state" />
                </th>
                <th className="text-right px-3 py-2.5 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('hospitalCount')}>
                  {t('stateComparison.chargemaster.hospitals')}<SortIcon k="hospitalCount" />
                </th>
                <th className="text-right px-3 py-2.5 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('medianMarkup')}>
                  {t('stateComparison.chargemaster.medianMarkup')}<SortIcon k="medianMarkup" />
                </th>
                <th className="text-right px-3 py-2.5 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('avgMarkup')}>
                  {t('stateComparison.chargemaster.avgMarkup')}<SortIcon k="avgMarkup" />
                </th>
                <th className="text-center px-3 py-2.5 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('grade')}>
                  {t('stateComparison.chargemaster.grade')}<SortIcon k="grade" />
                </th>
                <th className="text-center px-3 py-2.5 whitespace-nowrap">{t('stateComparison.chargemaster.gradeDistribution')}</th>
                {showCodeCols && (
                  <>
                    <th className="text-right px-3 py-2.5 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('cptMedian')}>
                      CPT<SortIcon k="cptMedian" />
                    </th>
                    <th className="text-right px-3 py-2.5 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('drgMedian')}>
                      DRG<SortIcon k="drgMedian" />
                    </th>
                    <th className="text-right px-3 py-2.5 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('revMedian')}>
                      REV<SortIcon k="revMedian" />
                    </th>
                    <th className="text-right px-3 py-2.5 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('drugMedian')}>
                      Drug<SortIcon k="drugMedian" />
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => {
                const mMarkup = rowMedianMarkup(row, baseline);
                const aMarkup = rowAvgMarkup(row, baseline);
                const grade = rowGrade(row, baseline);
                const gradeDist = rowGradeDist(row, baseline);
                const total = gradeDist.A + gradeDist.B + gradeDist.C + gradeDist.D + gradeDist.F;
                const isSelected = row.state === selectedState;

                return (
                  <tr
                    key={row.state}
                    className="cursor-pointer transition-colors"
                    style={{
                      backgroundColor: isSelected
                        ? (isAhp ? (isDark ? 'rgba(197,165,90,0.15)' : 'rgba(197,165,90,0.1)') : 'rgba(0,255,65,0.1)')
                        : (i % 2 === 0 ? cardBg : 'transparent'),
                      borderLeft: isSelected ? `3px solid ${isAhp ? '#C5A55A' : '#00ff41'}` : '3px solid transparent',
                    }}
                    onClick={() => onStateSelect(row.state === selectedState ? null : row.state)}
                    onDoubleClick={() => navigate(`/hospital-rankings/${row.state.toLowerCase()}`)}
                  >
                    <td className="px-3 py-2 font-medium whitespace-nowrap">
                      <span className="font-bold">{row.state}</span>
                      <span className="ml-2 opacity-60 text-xs hidden sm:inline">{STATE_NAMES[row.state]}</span>
                    </td>
                    <td className="text-right px-3 py-2 tabular-nums">{row.hospitalCount.toLocaleString()}</td>
                    <td className="text-right px-3 py-2 tabular-nums font-medium" style={{ backgroundColor: getMarkupBg(mMarkup) }}>
                      {formatPct(mMarkup)}
                    </td>
                    <td className="text-right px-3 py-2 tabular-nums" style={{ backgroundColor: getMarkupBg(aMarkup) }}>
                      {formatPct(aMarkup)}
                    </td>
                    <td className="text-center px-3 py-2">
                      <span
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-xs"
                        style={{ backgroundColor: gradeColor(grade), color: '#fff' }}
                      >
                        {grade}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex h-4 rounded-full overflow-hidden min-w-[100px]" title={`A:${gradeDist.A} B:${gradeDist.B} C:${gradeDist.C} D:${gradeDist.D} F:${gradeDist.F}`}>
                        {total > 0 && (
                          <>
                            {gradeDist.A > 0 && <div style={{ width: `${(gradeDist.A / total) * 100}%`, backgroundColor: '#22c55e' }} />}
                            {gradeDist.B > 0 && <div style={{ width: `${(gradeDist.B / total) * 100}%`, backgroundColor: '#84cc16' }} />}
                            {gradeDist.C > 0 && <div style={{ width: `${(gradeDist.C / total) * 100}%`, backgroundColor: '#eab308' }} />}
                            {gradeDist.D > 0 && <div style={{ width: `${(gradeDist.D / total) * 100}%`, backgroundColor: '#f97316' }} />}
                            {gradeDist.F > 0 && <div style={{ width: `${(gradeDist.F / total) * 100}%`, backgroundColor: '#ef4444' }} />}
                          </>
                        )}
                      </div>
                    </td>
                    {showCodeCols && (
                      <>
                        {(['cpt', 'drg', 'rev', 'drug'] as const).map((code) => {
                          const val = rowCodeMedian(row, code, baseline);
                          return (
                            <td key={code} className="text-right px-3 py-2 tabular-nums text-xs" style={{ backgroundColor: val != null ? getMarkupBg(val) : 'transparent' }}>
                              {formatPct(val)}
                            </td>
                          );
                        })}
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected state detail */}
      {selectedState && (() => {
        const st = stats.find(s => s.state === selectedState);
        if (!st) return null;
        const mMarkup = rowMedianMarkup(st, baseline);
        const best = rowBestHospital(st, baseline);
        const worst = rowWorstHospital(st, baseline);
        return (
          <div className="rounded-xl p-4" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold" style={{ color: textColor }}>
                {STATE_NAMES[selectedState] || selectedState}
              </h3>
              <button
                onClick={() => navigate(`/hospital-rankings/${selectedState.toLowerCase()}`)}
                className="text-xs px-3 py-1.5 rounded-full"
                style={{
                  backgroundColor: isAhp ? 'rgba(178,34,52,0.15)' : 'rgba(0,255,65,0.15)',
                  color: isAhp ? '#B22234' : '#00ff41',
                }}
              >
                {t('stateComparison.chargemaster.viewAllHospitals', { count: st.hospitalCount })} →
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div style={{ color: mutedText }} className="text-xs">{t('stateComparison.chargemaster.medianMarkupLabel', { baseline: baselineLabel })}</div>
                <div className="font-bold text-lg" style={{ color: textColor }}>{formatPct(mMarkup)}</div>
              </div>
              <div>
                <div style={{ color: mutedText }} className="text-xs">{t('stateComparison.chargemaster.grade')}</div>
                <div className="font-bold text-lg" style={{ color: gradeColor(rowGrade(st, baseline)) }}>
                  {rowGrade(st, baseline)}
                </div>
              </div>
              <div>
                <div style={{ color: mutedText }} className="text-xs">{t('stateComparison.chargemaster.bestHospital')}</div>
                <div className="font-medium text-xs truncate" style={{ color: textColor }}>
                  {best?.name || '—'}
                </div>
                <div className="text-xs" style={{ color: '#22c55e' }}>{formatPct(best?.markup ?? null)}</div>
              </div>
              <div>
                <div style={{ color: mutedText }} className="text-xs">{t('stateComparison.chargemaster.worstHospital')}</div>
                <div className="font-medium text-xs truncate" style={{ color: textColor }}>
                  {worst?.name || '—'}
                </div>
                <div className="text-xs" style={{ color: '#ef4444' }}>{formatPct(worst?.markup ?? null)}</div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
